import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPin } from 'lucide-react';
import { AnalysisParams } from './types';

interface BasicSettingsTabProps {
  fileId: string | null;
  params: AnalysisParams;
  filePositionSizes: string[];
  isGlobal: boolean;
  onUpdateParam: (key: keyof AnalysisParams, value: any) => void;
  onApplyMinPositionSize?: () => void;
}

export const BasicSettingsTab: React.FC<BasicSettingsTabProps> = ({
  fileId,
  params,
  filePositionSizes,
  isGlobal,
  onUpdateParam,
  onApplyMinPositionSize
}) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`tp-${fileId || 'global'}`}>Take Profit (USD)</Label>
          <Input
            id={`tp-${fileId || 'global'}`}
            type="number"
            step="0.01"
            value={params.tp_usd}
            onChange={(e) => onUpdateParam('tp_usd', parseFloat(e.target.value) || 0)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`sl-${fileId || 'global'}`}>Stop Loss (USD)</Label>
          <Input
            id={`sl-${fileId || 'global'}`}
            type="number"
            step="0.01"
            value={params.sl_usd}
            onChange={(e) => onUpdateParam('sl_usd', parseFloat(e.target.value) || 0)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`qty-${fileId || 'global'}`}>Quantity Base</Label>
          <div className="flex gap-2">
            <Input
              id={`qty-${fileId || 'global'}`}
              type="number"
              step="0.01"
              value={params.quantity_base}
              onChange={(e) => onUpdateParam('quantity_base', parseFloat(e.target.value) || 0)}
            />
            {!isGlobal && onApplyMinPositionSize && (
              <Button
                variant="outline"
                size="sm"
                onClick={onApplyMinPositionSize}
                className="text-xs whitespace-nowrap"
                title="Auto-fill from minimum position size"
              >
                <MapPin className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`capital-${fileId || 'global'}`}>Initial Capital</Label>
          <Input
            id={`capital-${fileId || 'global'}`}
            type="number"
            step="0.01"
            value={params.initial_capital}
            onChange={(e) => onUpdateParam('initial_capital', parseFloat(e.target.value) || 0)}
          />
        </div>
      </div>

      {!isGlobal && filePositionSizes.length > 0 && (
        <div className="space-y-2">
          <Label>Position Sizes to Include</Label>
          <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
            {filePositionSizes.map((size) => (
              <div key={size} className="flex items-center space-x-2">
                <Checkbox
                  id={`${fileId}-size-${size}`}
                  checked={params.selected_qty_values.includes(size)}
                  onCheckedChange={(checked) => {
                    const newSelected = checked
                      ? [...params.selected_qty_values, size]
                      : params.selected_qty_values.filter(s => s !== size);
                    onUpdateParam('selected_qty_values', newSelected);
                  }}
                />
                <Label htmlFor={`${fileId}-size-${size}`} className="text-sm">
                  {size}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};